/*
 * Self-hosted wishlist (replaces Swym Wishlist Plus, 2026-09-23).
 *
 * Config comes from snippets/prex-wishlist-config.liquid (window.PrexWishlistConfig).
 * Backend: Supabase edge function `wishlist`.
 *
 *   - Guests get a random uuid in localStorage; logged-in customers use their
 *     signed customer id. A guest list is merged into the customer's on login.
 *   - PDP: .prex-wishlist-bar (snippets/prex-wishlist-button.liquid) gets the
 *     store-wide count + toggle. data-count is set once the count is known;
 *     product-info.liquid waits on that before moving the bar into the trust row.
 *   - /pages/wishlist: #prex-wishlist-page lists the shopper's saved products.
 */
(function () {
  'use strict';

  var cfg = window.PrexWishlistConfig || {};
  if (!cfg.endpoint || window.PrexWishlist) return;

  var LS_GUEST = 'prexWl.guest';
  var SS_ITEMS = 'prexWl.items';

  function store(area, key, val) {
    try {
      var s = window[area];
      if (val === undefined) return s.getItem(key);
      if (val === null) s.removeItem(key);
      else s.setItem(key, val);
    } catch (e) { /* private mode / blocked storage */ }
    return null;
  }

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    var b = new Uint8Array(16);
    crypto.getRandomValues(b);
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    var h = Array.prototype.map.call(b, function (x) { return (x + 0x100).toString(16).slice(1); }).join('');
    return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20);
  }

  function guestId() {
    var g = store('localStorage', LS_GUEST);
    if (!g) { g = uuid(); store('localStorage', LS_GUEST, g); }
    return g;
  }

  function owner() {
    return cfg.customer ? { customer_id: cfg.customer.id, sig: cfg.customer.sig } : { guest: guestId() };
  }
  function ownerKey() {
    return cfg.customer ? 'c:' + cfg.customer.id : 'g:' + guestId();
  }

  // text/plain keeps this a "simple" CORS request (no preflight); the function
  // parses the body as JSON regardless of content type.
  function post(body) {
    body.owner = owner();
    return fetch(cfg.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error('wishlist ' + r.status);
      return r.json();
    });
  }

  // A guest who logs in keeps what they saved while logged out.
  var ready = Promise.resolve();
  if (cfg.customer) {
    var pendingGuest = store('localStorage', LS_GUEST);
    if (pendingGuest) {
      ready = post({ action: 'merge', guest: pendingGuest })
        .then(function () { store('localStorage', LS_GUEST, null); store('sessionStorage', SS_ITEMS, null); })
        .catch(function () { /* retried on the next page view */ });
    }
  }

  var items = null;
  var loading = null;

  function save() {
    store('sessionStorage', SS_ITEMS, JSON.stringify({ k: ownerKey(), items: items }));
  }

  function loadItems() {
    if (items) return Promise.resolve(items);
    if (loading) return loading;
    var cached = store('sessionStorage', SS_ITEMS);
    if (cached) {
      try {
        var c = JSON.parse(cached);
        if (c && c.k === ownerKey() && Array.isArray(c.items)) { items = c.items; return Promise.resolve(items); }
      } catch (e) { /* ignore bad cache */ }
    }
    loading = ready
      .then(function () { return post({ action: 'list' }); })
      .then(function (d) { items = d.items || []; save(); return items; })
      .catch(function () { return []; })
      .then(function (r) { loading = null; return r; });
    return loading;
  }

  function has(pid) {
    return !!items && items.some(function (i) { return String(i.product_id) === String(pid); });
  }

  function emit(pid, added) {
    document.dispatchEvent(new CustomEvent('prex:wishlist-change', { detail: { productId: pid, added: added } }));
  }

  function add(meta) {
    var pid = Number(meta.product_id);
    return loadItems().then(function () {
      if (has(pid)) return;
      items.unshift({ product_id: pid, variant_id: meta.variant_id || null, handle: meta.handle || null, created_at: new Date().toISOString() });
      save();
      emit(pid, true);
      return post({ action: 'add', product_id: pid, variant_id: meta.variant_id, handle: meta.handle })
        .catch(function (e) {
          items = items.filter(function (i) { return i.product_id !== pid; });
          save();
          emit(pid, false);
          throw e;
        });
    });
  }

  function remove(pid) {
    pid = Number(pid);
    return loadItems().then(function () {
      var prev = items;
      if (!has(pid)) return;
      items = items.filter(function (i) { return Number(i.product_id) !== pid; });
      save();
      emit(pid, false);
      return post({ action: 'remove', product_id: pid }).catch(function (e) {
        items = prev;
        save();
        emit(pid, true);
        throw e;
      });
    });
  }

  // ── PDP button ─────────────────────────────────────────────────────────
  function paintBar(bar) {
    var btn = bar.querySelector('.prex-wishlist-btn');
    var num = bar.querySelector('.prex-wishlist-num');
    var on = has(bar.getAttribute('data-product-id'));
    if (btn) {
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Add to wishlist');
    }
    if (num && bar.hasAttribute('data-count')) num.textContent = '(' + bar.getAttribute('data-count') + ')';
  }

  function initBar(bar) {
    if (bar.__prexWl) return;
    bar.__prexWl = true;
    var pid = bar.getAttribute('data-product-id');
    var seed = parseInt(bar.getAttribute('data-seed') || '0', 10) || 0;

    fetch(cfg.endpoint + '?product_id=' + encodeURIComponent(pid))
      .then(function (r) { return r.ok ? r.json() : { count: 0 }; })
      .catch(function () { return { count: 0 }; })
      .then(function (d) {
        bar.setAttribute('data-count', String((Number(d.count) || 0) + seed));
        paintBar(bar);
      });

    loadItems().then(function () { paintBar(bar); });

    var btn = bar.querySelector('.prex-wishlist-btn');
    if (!btn) return;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (btn.disabled) return;
      btn.disabled = true;
      var wasOn = has(pid);
      var op = wasOn ? remove(pid) : add({
        product_id: pid,
        variant_id: bar.getAttribute('data-variant-id'),
        handle: bar.getAttribute('data-handle')
      });
      if (bar.hasAttribute('data-count')) {
        var n = parseInt(bar.getAttribute('data-count'), 10) || 0;
        bar.setAttribute('data-count', String(Math.max(0, n + (wasOn ? -1 : 1))));
      }
      paintBar(bar);
      op.catch(function () {
        if (bar.hasAttribute('data-count')) {
          var m = parseInt(bar.getAttribute('data-count'), 10) || 0;
          bar.setAttribute('data-count', String(Math.max(0, m + (wasOn ? 1 : -1))));
        }
      }).then(function () {
        btn.disabled = false;
        paintBar(bar);
      });
    });
  }

  // ── Collection / search card heart (snippets/prex-wishlist-card.liquid) ──
  // One list request per page (cached in sessionStorage), no per-card count.
  function paintCard(btn) {
    var on = has(btn.getAttribute('data-product-id'));
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Add to wishlist');
    btn.setAttribute('title', on ? 'Remove from wishlist' : 'Add to wishlist');
  }

  // Lucky Box cards (data-show-count): one GET per card, printed after the heart.
  function paintCardCount(btn) {
    var n = btn.querySelector('.prex-wishlist-card__n');
    if (n && btn.hasAttribute('data-count')) n.textContent = btn.getAttribute('data-count');
  }
  function bumpCardCount(btn, delta) {
    if (!btn.hasAttribute('data-count')) return;
    var n = parseInt(btn.getAttribute('data-count'), 10) || 0;
    btn.setAttribute('data-count', String(Math.max(0, n + delta)));
    paintCardCount(btn);
  }

  function initCard(btn) {
    if (btn.__prexWl) return;
    btn.__prexWl = true;
    if (btn.hasAttribute('data-show-count')) {
      var seed = parseInt(btn.getAttribute('data-seed') || '0', 10) || 0;
      fetch(cfg.endpoint + '?product_id=' + encodeURIComponent(btn.getAttribute('data-product-id')))
        .then(function (r) { return r.ok ? r.json() : { count: 0 }; })
        .catch(function () { return { count: 0 }; })
        .then(function (d) {
          btn.setAttribute('data-count', String((Number(d.count) || 0) + seed));
          paintCardCount(btn);
        });
    }
    loadItems().then(function () { paintCard(btn); });
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (btn.disabled) return;
      btn.disabled = true;
      var pid = btn.getAttribute('data-product-id');
      loadItems().then(function () {
        var wasOn = has(pid);
        var op = wasOn ? remove(pid) : add({
          product_id: pid,
          variant_id: btn.getAttribute('data-variant-id'),
          handle: btn.getAttribute('data-handle')
        });
        paintCard(btn);
        bumpCardCount(btn, wasOn ? -1 : 1);
        return op.catch(function (e) { bumpCardCount(btn, wasOn ? 1 : -1); throw e; });
      }).catch(function () { /* optimistic state already rolled back */ }).then(function () {
        btn.disabled = false;
        paintCard(btn);
      });
    });
  }

  document.addEventListener('prex:wishlist-change', function () {
    document.querySelectorAll('.prex-wishlist-bar').forEach(paintBar);
    document.querySelectorAll('.prex-wishlist-card').forEach(paintCard);
  });

  // Facet filtering / sorting / infinite scroll swap the product grid in
  // without a page load; pick up the new cards' hearts.
  // setTimeout, not requestAnimationFrame: rAF never fires in a hidden tab, and a
  // grid swapped in while the tab is in the background would stay unwired.
  var initQueued = false;
  function queueInit() {
    if (initQueued) return;
    initQueued = true;
    setTimeout(function () { initQueued = false; initWidgets(); }, 0);
  }
  if (window.MutationObserver) {
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        if (muts[i].addedNodes.length) { queueInit(); return; }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  // ── /pages/wishlist ────────────────────────────────────────────────────
  function money(cents) {
    var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'CAD';
    return '$' + (Number(cents || 0) / 100).toFixed(2) + ' ' + cur;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return fetch(root + 'products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function renderPage(el) {
    var grid = el.querySelector('[data-prex-wishlist-grid]');
    var empty = el.querySelector('[data-prex-wishlist-empty]');
    var status = el.querySelector('[data-prex-wishlist-status]');
    loadItems().then(function (list) {
      return Promise.all(list.map(function (i) { return fetchProduct(i.handle); }));
    }).then(function (products) {
      if (status) status.hidden = true;
      var shown = products.filter(Boolean);
      grid.innerHTML = shown.map(function (p) {
        var img = p.featured_image ? p.featured_image + (p.featured_image.indexOf('?') > -1 ? '&' : '?') + 'width=400' : '';
        return '<div class="prex-wl-card" data-product-id="' + p.id + '">' +
          '<a class="prex-wl-img" href="' + esc(p.url) + '">' + (img ? '<img src="' + esc(img) + '" alt="' + esc(p.title) + '" loading="lazy">' : '') + '</a>' +
          '<a class="prex-wl-title" href="' + esc(p.url) + '">' + esc(p.title) + '</a>' +
          '<div class="prex-wl-price">' + money(p.price) + (p.available ? '' : ' <span class="prex-wl-oos">Sold out</span>') + '</div>' +
          '<button type="button" class="prex-wl-remove" data-remove="' + p.id + '">Remove</button>' +
          '</div>';
      }).join('');
      empty.hidden = shown.length > 0;
    });

    if (el.__prexWlBound) return;
    el.__prexWlBound = true;
    grid.addEventListener('click', function (e) {
      var b = e.target.closest('[data-remove]');
      if (!b) return;
      b.disabled = true;
      var card = b.closest('.prex-wl-card');
      remove(b.getAttribute('data-remove')).then(function () {
        if (card) card.remove();
        empty.hidden = grid.children.length > 0;
      }).catch(function () { b.disabled = false; });
    });
  }

  function initWidgets() {
    document.querySelectorAll('.prex-wishlist-bar').forEach(initBar);
    document.querySelectorAll('.prex-wishlist-card').forEach(initCard);
  }

  function init() {
    initWidgets();
    var page = document.getElementById('prex-wishlist-page');
    if (page) renderPage(page);
  }

  window.PrexWishlist = { add: add, remove: remove, has: has, load: loadItems };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
